import type { components } from '@connected/api-client/api.generated';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { api, client } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { usePatcher } from '@connected/api-client/hooks/use-patcher';
import { debounceTime } from '@connected/api-client/patcher';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import { NumberField } from '@connected/form-fields/components/number';
import {
    MultipleOptionField,
    MultipleOptionItem,
    SingleCheckOptionItem,
    SingleHorizontalOptionItem,
    SingleOptionField,
    SingleOptionItem,
} from '@connected/form-fields/components/option';
import { Button } from '@connected/ui/components/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@connected/ui/components/command';
import { FileInput } from '@connected/ui/components/file';
import { Input } from '@connected/ui/components/input';
import { Popout, PopoutBody, PopoutContent, PopoutFooter, PopoutHeader, PopoutTitle } from '@connected/ui/components/popout';
import { Popover, PopoverContent, PopoverTrigger } from '@connected/ui/components/popover';
import { Textarea } from '@connected/ui/components/textarea';
import { Page, Section } from '@connected/ui/features/layouts';
import { booleanDefaultValue } from '@connected/ui/lib/utils';
import { useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { Edit, Loader2, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Option } from '@/hooks/use-option';

import { ElectricalProjectImage } from '@/components/electrical-project-image';
import { ElseField } from '@/components/else-field';
import { InformationLine } from '@/components/information-line';
import { LocationMap } from '@/components/location-map';
import { FooterNavigation } from '@/components/navigation/footer-navigation';
import { useCondition } from '@/config/conditions';
import { useElectricalProjectFileDelete } from '@/hooks/use-electrical-project-file-delete';
import { useElectricalProjectFileUpload } from '@/hooks/use-electrical-project-file-upload';
import { useFormTouched } from '@/hooks/use-form-touched';
import { useSelectableOptions } from '@/hooks/use-option';
import { queryClient } from '@/lib/query-client';
import { showConfirm } from '@/stores/confirmer';
import { useOffline } from '@/stores/network-status';

export const Route = createFileRoute(
    '/(auth)/projects/$projectVersionId/applications/objects/',
)({
    component: ObjectsPage,
    loader: ({ context, params }) => {
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.request.objectOptions({
            electricalProjectVersionId: params.projectVersionId,
        }));
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.selectListsItems.selectListsItemsGetOptions({
            electricalProjectVersionId: params.projectVersionId,
            listKind: 'BuildingFunction',
        }));
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.selectListsItems.selectListsItemsGetOptions({
            electricalProjectVersionId: params.projectVersionId,
            listKind: 'ObjectSpecialSpace',
        }));
    },
});

type ElectricalProjectInspectionPlanCreatorKind
    = | 'InstallationAdministrator'
        | 'BuildingOwnerOrAdministrator'
        | 'Customer'
        | 'InspectionCompany';

type ObjectLocationForm = {
    name: string;
    totalFloorArea: number | null;
    buildingConstructionYear: number | null;
    email: string;
    telephoneNumber: string;
    buildingCount: number | null;
    distributionBoardCount: number | null;
    controlBoxCount: number | null;
    electricalInstallationConstructionYear: number | null;
    remarks?: string;
    specialRooms?: string;
    inspectionPlanCreatorKind: ElectricalProjectInspectionPlanCreatorKind | null;
};

type ObjectBagForm = {
    ObjectKind: components['schemas']['BAGObjectKind'];
    BAGBuildingIdentifier: string;
    BAGAddressIdentifier?: string | null;
};

type AddressForm = {
    street: string;
    streetNumber: string;
    locality: string;
    postalCode: string;
};

function ObjectsPage() {
    const canAccessSolarInspection = useCondition('canAccessSolarInspection');

    return (
        <Page className="flex flex-col gap-6">
            <ObjectForm />
            {canAccessSolarInspection && <Scope12ObjectForm />}
        </Page>
    );
}

function ObjectForm() {
    const { t } = useTranslation('applications');

    const canAccessSolarInspection = useCondition('canAccessSolarInspection');

    const isUserOffline = useOffline();

    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);

    const params = Route.useParams();

    const objectInformation = useQuery(elektraAppQueryOptions.project.request.objectOptions({ electricalProjectVersionId: params.projectVersionId }));

    const objectInformationData = useMemo(() => objectInformation?.data?.ProjectObject, [objectInformation?.data?.ProjectObject]);

    // Mutations
    const objectInformationMutation = api.useMutation('put', '/api/v2/electrical/projects/objects/edit', {
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: elektraAppQueryOptions.project.request.objectOptions({
                    electricalProjectVersionId: params.projectVersionId,
                }).queryKey,
            });
            queryClient.invalidateQueries(elektraAppQueryOptions.project.recapOptions({
                electricalProjectVersionId: params.projectVersionId,
            }));
        },
    });

    const objectForm = useForm<ObjectLocationForm, ZodValidator>({
        defaultValues: {
            name: objectInformationData?.Name || '',
            email: objectInformationData?.Email || '',
            telephoneNumber: objectInformationData?.TelephoneNumber || '',
            totalFloorArea: objectInformationData?.TotalFloorArea || null, // TODO: Voor scope 10, scope 8 en nen 3140 niet verplicht
            buildingConstructionYear: objectInformationData?.BuildingConstructionYear || null,
            buildingCount: objectInformationData?.BuildingCount || null,
            distributionBoardCount: objectInformationData?.DistributionBoardCount || null,
            controlBoxCount: objectInformationData?.ControlBoxCount || null,
            electricalInstallationConstructionYear: objectInformationData?.ElectricalInstallationConstructionYear || null,
            remarks: objectInformationData?.Remarks || '',
            inspectionPlanCreatorKind: objectInformationData?.InspectionPlanCreatorKind || null,
        },
        onSubmit: async ({ value }) => {
            return objectInformationMutation.mutateAsync({
                body: {
                    ElectricalProjectVersionId: params.projectVersionId,
                    Name: value.name,
                    Email: value.email,
                    TelephoneNumber: value.telephoneNumber,
                    TotalFloorArea: value.totalFloorArea,
                    BuildingConstructionYear: value.buildingConstructionYear,
                    BuildingCount: value.buildingCount,
                    DistributionBoardCount: value.distributionBoardCount,
                    ControlBoxCount: value.controlBoxCount,
                    ElectricalInstallationConstructionYear: value.electricalInstallationConstructionYear,
                    Remarks: value.remarks,
                    InspectionPlanCreatorKind: value.inspectionPlanCreatorKind,

                },
            });
        },
        validatorAdapter: zodValidator(),
    });

    const buildingFunctionOptionsData = useQuery(
        elektraAppQueryOptions.project.selectListsItems.selectListsItemsGetOptions({
            electricalProjectVersionId: params.projectVersionId,
            listKind: 'BuildingFunction',
        }),
    );
    const objectSpecialSpacesOptionsData = useQuery(
        elektraAppQueryOptions.project.selectListsItems.selectListsItemsGetOptions({
            electricalProjectVersionId: params.projectVersionId,
            listKind: 'ObjectSpecialSpace',
        }),
    );
    const objectInspectionExclusionOptionsData = useQuery(
        elektraAppQueryOptions.project.selectListsItems.selectListsItemsGetOptions({
            electricalProjectVersionId: params.projectVersionId,
            listKind: 'InspectionExclusion',
        }),
    );

    const buildingFunctionOptions = useSelectableOptions({
        options:
            buildingFunctionOptionsData?.data?.Items?.map(item => ({
                value: item.ElectricalProjectSelectListItemId!,
                label: item.Label!,
            })) ?? [],
        initialSelectedValues:
            buildingFunctionOptionsData?.data?.Items?.filter(
                x => x.Selected,
            )?.map(x => x.ElectricalProjectSelectListItemId!) ?? [],
        selectItemType: 'BuildingFunction',
        electricalProjectVersionId: params.projectVersionId,
        parentKind: 'Object',
        scopeIdentifiers: {
            ElectricalProjectVersionId: params.projectVersionId,
        },
    });
    const objectSpecialSpaces = useSelectableOptions({
        options:
            (objectSpecialSpacesOptionsData?.data?.Items?.map(item => ({
                value: item.ElectricalProjectSelectListItemId!,
                label: item.Label!,
            })) as Option<string>[]) ?? [],
        initialSelectedValues:
            objectSpecialSpacesOptionsData?.data?.Items?.filter(
                x => x.Selected,
            )?.map(x => x.ElectricalProjectSelectListItemId!) ?? [],
        selectItemType: 'ObjectSpecialSpace',
        electricalProjectVersionId: params.projectVersionId,
        parentKind: 'Object',
        scopeIdentifiers: {
            ElectricalProjectVersionId: params.projectVersionId,
        },
    });
    const objectInspectionExclusion = useSelectableOptions({
        options:
            (objectInspectionExclusionOptionsData?.data?.Items?.map(item => ({
                value: item.ElectricalProjectSelectListItemId!,
                label: item.Label!,
            })) as Option<string>[]) ?? [],
        initialSelectedValues:
            objectInspectionExclusionOptionsData?.data?.Items?.filter(
                x => x.Selected,
            )?.map(x => x.ElectricalProjectSelectListItemId!) ?? [],
        selectItemType: 'InspectionExclusion',
        electricalProjectVersionId: params.projectVersionId,
        parentKind: 'Object',
        scopeIdentifiers: {
            ElectricalProjectVersionId: params.projectVersionId,
        },
    });

    const { patchField } = usePatcher('/api/v2/electrical/projects/objects/patch', {
        onMutate: async (variables) => {
            const objectQueryOptions = elektraAppQueryOptions.project.request.objectOptions({
                electricalProjectVersionId: params.projectVersionId,
            });

            await queryClient.cancelQueries(objectQueryOptions);

            const previousData = queryClient.getQueryData(objectQueryOptions.queryKey) as components['schemas']['EditElectricalProjectObjectQueryResult'];

            if (!previousData || !variables?.body) {
                return;
            }

            const newData: components['schemas']['EditElectricalProjectObjectQueryResult'] = {
                ...previousData,
                ProjectObject: {
                    ...previousData.ProjectObject,
                    ...variables.body,
                    ElectricalProjectVersionId: params.projectVersionId,
                },
            };

            queryClient.setQueryData(objectQueryOptions.queryKey, newData);

            return { previousData, queryKey: objectQueryOptions.queryKey };
        },
        onSettled: () => {
            queryClient.invalidateQueries(elektraAppQueryOptions.project.request.objectOptions({
                electricalProjectVersionId: params.projectVersionId,
            }));
            queryClient.invalidateQueries(elektraAppQueryOptions.project.quotation.quotationOptions({
                electricalProjectVersionId: params.projectVersionId,
            }));
            queryClient.invalidateQueries(elektraAppQueryOptions.project.recapOptions({
                electricalProjectVersionId: params.projectVersionId,
            }));
        },
    }, { body: { ElectricalProjectVersionId: params.projectVersionId } });

    const fileUpload = useElectricalProjectFileUpload({
        endpoint: '/api/v2/electrical/projects/files/upload/objectimage',
        mutationOptions: {
            onMutate: async (file) => {
                const queryOptions = elektraAppQueryOptions.project.request.objectOptions({ electricalProjectVersionId: params.projectVersionId });

                await queryClient.cancelQueries(queryOptions);

                const previousData = queryClient.getQueryData<components['schemas']['EditElectricalProjectObjectQueryResult']>(queryOptions.queryKey);

                if (!previousData || !previousData.ProjectObject) {
                    return;
                }

                const newData: components['schemas']['EditElectricalProjectObjectQueryResult'] = {
                    ...previousData,
                    ProjectObject: {
                        ...previousData.ProjectObject,
                        ElectricalProjectVersionId: params.projectVersionId,
                        ObjectImage: {
                            ...previousData.ProjectObject.ObjectImage,
                            ElectricalProjectFileId: file.ElectricalProjectFileId,
                        },
                    },
                };

                queryClient.setQueryData(queryOptions.queryKey, newData);
            },
            onSettled: () => {
                queryClient.invalidateQueries(elektraAppQueryOptions.project.request.objectOptions({
                    electricalProjectVersionId: params.projectVersionId,
                }));
            },
            scope: {
                id: params.projectVersionId,
            },
        },
    });
    const fileDelete = useElectricalProjectFileDelete({
        mutationOptions: {
            onMutate: async (_fileId) => {
                const queryOptions = elektraAppQueryOptions.project.request.objectOptions({
                    electricalProjectVersionId: params.projectVersionId,
                });

                await queryClient.cancelQueries(queryOptions);

                const previousData = queryClient.getQueryData<components['schemas']['EditElectricalProjectObjectQueryResult']>(queryOptions.queryKey);

                if (!previousData || !previousData.ProjectObject) {
                    return;
                }

                const newData: components['schemas']['EditElectricalProjectObjectQueryResult'] = {
                    ...previousData,
                    ProjectObject: {
                        ...previousData.ProjectObject,
                        ElectricalProjectVersionId: params.projectVersionId,
                        ObjectImage: {
                            ...previousData.ProjectObject.ObjectImage,
                            ElectricalProjectFileId: null,
                        },
                    },
                };

                queryClient.setQueryData(queryOptions.queryKey, newData);
            },
            onSettled: async () => {
                await queryClient.invalidateQueries(elektraAppQueryOptions.project.request.objectOptions({
                    electricalProjectVersionId: params.projectVersionId,
                }));
            },
        },
    });

    // check or address or addressnumber or zipcode or locality is filled
    const isAddressFilled = useMemo(() => {
        return !!(
            objectInformationData?.Street
            && objectInformationData?.StreetNumber
            && objectInformationData?.PostalCode
            && objectInformationData?.Locality
        );
    }, [objectInformationData]);

    return (
        <>
            {!isAddressFilled && (
                <Section>
                    <Section.Header>
                        <Section.Title>{t('objects.layout.navigation.objects.label')}</Section.Title>
                    </Section.Header>
                    <Section.Content>
                        <AdressModal isOpen={isAddressModalOpen} setIsOpen={setIsAddressModalOpen} />
                    </Section.Content>
                </Section>
            )}

            {/* Visiable if building selected */}
            {isAddressFilled && (
                <>
                    <Section>
                        <Section.Header>
                            <Section.Title>{t('objects.layout.navigation.objects.label')}</Section.Title>
                        </Section.Header>
                        <Section.Content>
                            <objectForm.Field
                                name="name"
                                validators={{
                                    onChange: validators.patchElectricalProjectObjectCommand.name,
                                    onChangeAsync: patchField('Name'),
                                    onChangeAsyncDebounceMs: debounceTime,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label label={t('objects.location.form.name.label')}>
                                        <Input
                                            name={name}
                                            value={state.value}
                                            onChange={values => handleChange(values.target.value)}
                                            placeholder={t('objects.location.form.name.placeholder')}
                                            disabled={isUserOffline}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </objectForm.Field>

                            {objectInformationData?.ObjectImage?.ElectricalProjectFileId
                                ? (
                                        <Label label={t('objects.location.form.image.label')} htmlFor="file-input">
                                            <div className="relative flex items-center justify-between gap-2">
                                                <ElectricalProjectImage
                                                    className="h-[112px] w-full rounded-lg"
                                                    query={{
                                                        ElectricalProjectFileId: objectInformationData?.ObjectImage?.ElectricalProjectFileId ?? '',
                                                        FileId: objectInformationData?.ObjectImage?.File?.FileId ?? null,
                                                    }}
                                                    alt={t('objects.location.form.image.label')}
                                                    enabled={!!objectInformationData?.ObjectImage?.ElectricalProjectFileId}
                                                    expandable
                                                />
                                                <Button
                                                    className="absolute top-1 right-1 bg-background/50"
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={async (e) => {
                                                        e.stopPropagation();

                                                        fileDelete.delete(objectInformationData?.ObjectImage?.ElectricalProjectFileId ?? '');
                                                    }}
                                                    disabled={isUserOffline || fileDelete.isDeleting}
                                                >
                                                    {fileDelete.isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 />}
                                                </Button>
                                            </div>
                                        </Label>
                                    )
                                : (
                                        <Label label={t('objects.location.form.image.label')}>
                                            <FileInput
                                                accept={fileUpload.accept ?? undefined}
                                                onChange={(fileList) => {
                                                    const file = fileList[0];

                                                    if (!file) {
                                                        return;
                                                    }

                                                    fileUpload.upload({ file });
                                                }}
                                                loading={fileUpload.isLoading}
                                                disabled={fileUpload.isDisabled || isUserOffline}
                                                mode="both"
                                            />
                                        </Label>
                                    )}

                            <div className="flex flex-col gap-2">
                                {(objectInformationData?.Street && objectInformationData?.StreetNumber) && (
                                    <InformationLine label="Straatnaam en huisnummer" value={`${objectInformationData.Street} ${objectInformationData.StreetNumber}`} />
                                )}
                                {(objectInformationData?.PostalCode && objectInformationData?.Locality) && (
                                    <InformationLine label="Postcode en plaats" value={`${objectInformationData.PostalCode} ${objectInformationData.Locality}`} />
                                )}
                                {!isAddressModalOpen && objectInformationData?.Coordinate && objectInformationData?.Coordinate.GpsLongitude && objectInformationData?.Coordinate.GpsLatitude && (
                                    <LocationMap
                                        latitude={objectInformationData.Coordinate.GpsLatitude}
                                        longitude={objectInformationData.Coordinate.GpsLongitude}
                                        address={`${objectInformationData.Street} ${objectInformationData.StreetNumber}, ${objectInformationData.PostalCode} ${objectInformationData.Locality}`}
                                    />
                                )}
                                <AdressModal isOpen={isAddressModalOpen} setIsOpen={setIsAddressModalOpen} />
                            </div>

                            <div className="grid  grid-cols-1 sm:grid-cols-2 gap-4">
                                <objectForm.Field
                                    name="totalFloorArea"
                                    validators={{
                                        onChange: validators.patchElectricalProjectObjectCommand.totalFloorArea,
                                        onChangeAsync: patchField('TotalFloorArea'),
                                        onChangeAsyncDebounceMs: debounceTime,
                                    }}
                                >
                                    {({ handleChange, state, name }) => (
                                        <Label label={t('objects.location.form.total-floorarea.label')}>
                                            <NumberField
                                                name={name}
                                                value={state.value}
                                                onChange={handleChange}
                                                placeholder={t('objects.location.form.total-floorarea.placeholder')}
                                                disabled={isUserOffline}
                                            />
                                            <FieldInfo state={state} />
                                        </Label>
                                    )}
                                </objectForm.Field>
                                <objectForm.Field
                                    name="buildingConstructionYear"
                                    validators={{
                                        onChange:
                                        validators.patchElectricalProjectObjectCommand.buildingConstructionYear,
                                        onChangeAsync: patchField('BuildingConstructionYear'),
                                        onChangeAsyncDebounceMs: debounceTime,
                                    }}
                                >
                                    {({ handleChange, state, name }) => (
                                        <Label label={t('objects.location.form.building-construction-year.label')}>
                                            <NumberField
                                                name={name}
                                                value={state.value}
                                                onChange={handleChange}
                                                placeholder={t('objects.location.form.building-construction-year.placeholder')}
                                                disabled={isUserOffline}
                                            />
                                            <FieldInfo state={state} />
                                        </Label>
                                    )}
                                </objectForm.Field>
                                <objectForm.Field
                                    name="email"
                                    validators={{
                                        onChange: validators.patchElectricalProjectObjectCommand.email,
                                        onChangeAsync: patchField('Email'),
                                        onChangeAsyncDebounceMs: debounceTime,
                                    }}
                                >
                                    {({ handleChange, state, name }) => (
                                        <Label label={t('objects.location.form.generalEmail.label')}>
                                            <Input
                                                name={name}
                                                value={state.value}
                                                onChange={values => handleChange(values.target.value)}
                                                placeholder={t('objects.location.form.generalEmail.placeholder')}
                                                disabled={isUserOffline}
                                            />
                                            <FieldInfo state={state} />
                                        </Label>
                                    )}
                                </objectForm.Field>
                                <objectForm.Field
                                    name="telephoneNumber"
                                    validators={{
                                        onChange: validators.patchElectricalProjectObjectCommand.telephoneNumber,
                                        onChangeAsync: patchField('TelephoneNumber'),
                                        onChangeAsyncDebounceMs: debounceTime,
                                    }}
                                >
                                    {({ handleChange, state, name }) => (
                                        <Label label={t('objects.location.form.generalPhone.label')}>
                                            <Input
                                                name={name}
                                                value={state.value}
                                                onChange={values => handleChange(values.target.value)}
                                                placeholder={t('objects.location.form.generalPhone.placeholder')}
                                                disabled={isUserOffline}
                                            />
                                            <FieldInfo state={state} />
                                        </Label>
                                    )}
                                </objectForm.Field>
                            </div>
                        </Section.Content>
                    </Section>
                    <Section>
                        <Section.Header>
                            <Section.Title>
                                {t('objects.layout.navigation.scope.label')}
                            </Section.Title>
                        </Section.Header>
                        <Section.Content>
                            <objectForm.Field
                                name="buildingCount"
                                validators={{
                                    onChange: validators.patchElectricalProjectObjectCommand.buildingCount,
                                    onChangeAsync: patchField('BuildingCount'),
                                    onChangeAsyncDebounceMs: debounceTime,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label label={t('objects.scope.form.numberOfBuildings.label')}>
                                        <NumberField
                                            name={name}
                                            value={state.value}
                                            onChange={handleChange}
                                            placeholder={t('objects.scope.form.numberOfBuildings.placeholder')}
                                            disabled={isUserOffline}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </objectForm.Field>
                            <objectForm.Field
                                name="distributionBoardCount"
                                validators={{
                                    onChange: validators.patchElectricalProjectObjectCommand.distributionBoardCount,
                                    onChangeAsync: patchField('DistributionBoardCount'),
                                    onChangeAsyncDebounceMs: debounceTime,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label
                                        label={t(
                                            'objects.scope.form.numberOfDistributors.label',
                                        )}
                                    >
                                        <NumberField
                                            name={name}
                                            value={state.value}
                                            onChange={handleChange}
                                            placeholder={t('objects.scope.form.numberOfDistributors.placeholder')}
                                            disabled={isUserOffline}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </objectForm.Field>
                            <objectForm.Field
                                name="controlBoxCount"
                                validators={{
                                    onChange: validators.patchElectricalProjectObjectCommand.controlBoxCount,
                                    onChangeAsync: patchField('ControlBoxCount'),
                                    onChangeAsyncDebounceMs: debounceTime,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label label={t('objects.scope.form.numberOfControlCabinets.label')}>
                                        <NumberField
                                            name={name}
                                            value={state.value}
                                            onChange={handleChange}
                                            placeholder={t('objects.scope.form.numberOfControlCabinets.placeholder')}
                                            disabled={isUserOffline}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </objectForm.Field>
                            <objectForm.Field
                                name="electricalInstallationConstructionYear"
                                validators={{
                                    onChange: validators.patchElectricalProjectObjectCommand.electricalInstallationConstructionYear,
                                    onChangeAsync: patchField('ElectricalInstallationConstructionYear'),
                                    onChangeAsyncDebounceMs: debounceTime,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label label={t('objects.scope.form.installationYear.label')}>
                                        <NumberField
                                            name={name}
                                            value={state.value}
                                            onChange={handleChange}
                                            placeholder={t('objects.scope.form.installationYear.placeholder')}
                                            disabled={isUserOffline}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </objectForm.Field>

                            <Label label={t('objects.scope.form.userFunction.label')}>
                                <MultipleOptionField
                                    handleChange={buildingFunctionOptions.onChange}
                                    placeholder={t('objects.scope.form.userFunction.placeholder')}
                                    state={{
                                        value: buildingFunctionOptions?.selectedValues,
                                    }}
                                    options={buildingFunctionOptions?.options}
                                    render={MultipleOptionItem}
                                    dropdown
                                    disabled={isUserOffline}
                                    search

                                />
                            </Label>
                            {buildingFunctionOptionsData.data?.Items?.filter(x => x.IsCustomInput === true && buildingFunctionOptions.selectedValues.includes(x.ElectricalProjectSelectListItemId!))?.map(item => (
                                <OtherField
                                    key={item.ElectricalProjectSelectListItemId}
                                    label={item.Label!}
                                    item={item}
                                />
                            ))}
                            <objectForm.Field
                                name="remarks"
                                validators={{
                                    onChange:
                                    validators.patchElectricalProjectObjectCommand.remarks,
                                    onChangeAsync: patchField('Remarks'),
                                    onChangeAsyncDebounceMs: debounceTime,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label
                                        label={t('objects.scope.form.specialNotes.label')}
                                    >
                                        <Textarea
                                            name={name}
                                            value={state.value}
                                            onChange={values => handleChange(values.target.value)}
                                            placeholder={t('objects.scope.form.specialNotes.placeholder')}
                                            disabled={isUserOffline}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </objectForm.Field>
                            <Label label={t('objects.scope.form.specialRooms.label')}>
                                <MultipleOptionField
                                    handleChange={objectSpecialSpaces.onChange}
                                    placeholder={t('objects.scope.form.specialRooms.placeholder')}
                                    state={{ value: objectSpecialSpaces.selectedValues }}
                                    options={objectSpecialSpaces?.options}
                                    render={MultipleOptionItem}
                                    dropdown
                                    disabled={isUserOffline}
                                    search
                                />
                            </Label>

                            {objectSpecialSpacesOptionsData.data?.Items?.filter(x => x.IsCustomInput === true && objectSpecialSpaces.selectedValues.includes(x.ElectricalProjectSelectListItemId!))?.map(item => (
                                <OtherField
                                    key={item.ElectricalProjectSelectListItemId}
                                    label={item.Label!}
                                    item={item}
                                />
                            ))}

                            <Label label="Uitsluitingen">
                                <MultipleOptionField
                                    handleChange={objectInspectionExclusion.onChange}
                                    placeholder="Noteer Uitsluitingen"
                                    state={{ value: objectInspectionExclusion.selectedValues }}
                                    options={objectInspectionExclusion?.options}
                                    render={MultipleOptionItem}
                                    dropdown
                                    disabled={isUserOffline}
                                    search
                                />
                            </Label>

                            {objectInspectionExclusionOptionsData.data?.Items?.filter(x => x.IsCustomInput === true && objectInspectionExclusion.selectedValues.includes(x.ElectricalProjectSelectListItemId!))?.map(item => (
                                <OtherField
                                    key={item.ElectricalProjectSelectListItemId}
                                    label={item.Label!}
                                    item={item}
                                />
                            ))}
                            <objectForm.Field
                                name="inspectionPlanCreatorKind"
                                validators={{
                                    onChange:
                                validators.patchElectricalProjectObjectCommand.inspectionPlanCreatorKind,
                                    onChangeAsync: patchField('InspectionPlanCreatorKind'),
                                    onChangeAsyncDebounceMs: debounceTime,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label htmlFor="inspectionPlanCreatorKind" label={t('objects.scope.form.inspectionPlanPreparedBy.label')}>
                                        <SingleOptionField
                                            name={name}
                                            handleChange={handleChange}
                                            state={state}
                                            options={[
                                                { label: 'Installatieverantwoordelijke', value: 'InstallationAdministrator' },
                                                { label: 'Eigenaar', value: 'BuildingOwnerOrAdministrator' },
                                                { label: 'Opdrachtgever', value: 'Customer' },
                                                { label: 'Inspectiebedrijf', value: 'InspectionCompany' },
                                            ]}
                                            render={SingleOptionItem}
                                            disabled={isUserOffline}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </objectForm.Field>
                        </Section.Content>
                        {!canAccessSolarInspection && <Footer />}
                    </Section>
                </>
            )}
        </>
    );
}

function Footer() {
    return (
        <Section.Footer>
            <FooterNavigation />
        </Section.Footer>
    );
}

function AdressModal({ isOpen, setIsOpen }: { isOpen: boolean; setIsOpen: (open: boolean) => void }) {
    const { t } = useTranslation('applications');
    const isUserOffline = useOffline();

    const [addressSearch, setAddressSearch] = useState<string>('');
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [addressResults, setAddressResults] = useState<components['schemas']['ListAddressBAGApiQueryResultAddress'][]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isCustom, setIsCustom] = useState(false);

    const params = Route.useParams();

    // Fetch addresses when search changes
    useEffect(() => {
        const searchAddresses = async () => {
            if (addressSearch.length <= 3) {
                setAddressResults([]);
                return;
            }

            setIsSearching(true);
            try {
                const { data, error } = await client.GET(
                    '/api/v2/cadastral/nl/lvbag/buildingsaddresses/list',
                    {
                        params: {
                            query: {
                                Search: addressSearch,
                                PageNumber: 1,
                                PageSize: 100,
                            },
                        },
                    },
                );

                if (data && !error) {
                    setAddressResults(data?.Addresses ?? []);
                }
                else {
                    setAddressResults([]);
                }
            }
            catch {
                setAddressResults([]);
            }
            finally {
                setIsSearching(false);
            }
        };

        const timeoutId = setTimeout(searchAddresses, 500);
        return () => clearTimeout(timeoutId);
    }, [addressSearch]);

    const objectBagMutation = api.useMutation(
        'put',
        '/api/v2/electrical/projects/objects/lvbag/create',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(elektraAppQueryOptions.project.request.objectOptions({
                    electricalProjectVersionId: params.projectVersionId,
                }));

                setIsOpen(false);
            },
        },
    );

    const bagObjectForm = useForm<ObjectBagForm, ZodValidator>({
        defaultValues: {
            ObjectKind: 'Address',
            BAGBuildingIdentifier: '',
            BAGAddressIdentifier: null,
        },
        onSubmit: async ({ value }) => {
            return objectBagMutation.mutateAsync({
                body: {
                    ElectricalProjectVersionId: params.projectVersionId,
                    ObjectKind: value.ObjectKind,
                    BAGBuildingIdentifier: value.BAGBuildingIdentifier,
                    BAGAddressIdentifier: value.BAGAddressIdentifier,
                },
            });
        },
    });

    const objectInformation = useQuery(elektraAppQueryOptions.project.request.objectOptions({
        electricalProjectVersionId: params.projectVersionId,
    }));
    const objectInformationData = objectInformation?.data?.ProjectObject;

    const defaultValues: AddressForm = {
        street: objectInformationData?.Street || '',
        streetNumber: objectInformationData?.StreetNumber || '',
        locality: objectInformationData?.Locality || '',
        postalCode: objectInformationData?.PostalCode || '',
    };

    const editAddressMutation = api.useMutation('put', '/api/v2/electrical/projects/objects/edit/address', {
        onSuccess: () => {
            queryClient.invalidateQueries(elektraAppQueryOptions.project.request.objectOptions({
                electricalProjectVersionId: params.projectVersionId,
            }));
            queryClient.invalidateQueries(elektraAppQueryOptions.project.recapOptions({
                electricalProjectVersionId: params.projectVersionId,
            }));

            setIsOpen(false);
        },
    });

    const { Field, handleSubmit, reset, store } = useForm<
        AddressForm,
        ZodValidator
    >({
        defaultValues,
        onSubmit: ({ value }) => {
            return editAddressMutation.mutate({
                body: {
                    ElectricalProjectVersionId: params.projectVersionId,
                    Street: value.street,
                    StreetNumber: value.streetNumber,
                    Locality: value.locality,
                    PostalCode: value.postalCode,
                },
            });
        },
        validatorAdapter: zodValidator(),
    });

    const isFormTouched = useFormTouched(store, defaultValues);

    async function handleClose() {
        if (isFormTouched) {
            if (await showConfirm('Weet je zeker dat je wilt sluiten?')) {
                setIsOpen(false);
                reset();
                editAddressMutation.reset();
            }
        }
        else {
            setIsOpen(false);
            reset();
            editAddressMutation.reset();
        }
    }

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <Button variant="link" disabled={isUserOffline} onClick={() => setIsOpen(true)}>
                {(!objectInformationData?.Street && !objectInformationData?.StreetNumber && !objectInformationData?.Locality && !objectInformationData?.PostalCode)
                    ? (
                            <div className="flex flex-row gap-2 items-center justify-center w-full">
                                <Plus />
                                <span>
                                    Adres toevoegen
                                </span>
                            </div>
                        )
                    : (
                            <div className="flex flex-row gap-2 items-center justify-center w-full">
                                <Edit />
                                <span>
                                    Adres bewerken
                                </span>
                            </div>
                        )}
            </Button>
            <PopoutContent>
                <PopoutHeader>
                    <PopoutTitle>
                        {(!objectInformationData?.Street && !objectInformationData?.StreetNumber && !objectInformationData?.Locality && !objectInformationData?.PostalCode)
                            ? 'Adres toevoegen'
                            : 'Adres bewerken'}
                    </PopoutTitle>
                </PopoutHeader>
                <PopoutBody>

                    {!isCustom && (
                        <div className="flex flex-col gap-4">
                            <Label label="Zoek adres in Kadaster">
                                <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            role="combobox"
                                            aria-expanded={isPopoverOpen}
                                            className="w-full justify-between"
                                            disabled={isUserOffline}
                                        >
                                            {addressSearch || 'Zoek adres op plaats, postcode, straat en huisnummer'}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                                        <Command className="w-full">
                                            <CommandInput
                                                placeholder="Zoek adres op plaats, postcode, straat en huisnummer"
                                                value={addressSearch}
                                                className="w-full"
                                                onValueChange={(value) => {
                                                    setAddressSearch(value);
                                                }}
                                                disabled={isUserOffline}
                                            />
                                            <CommandList>
                                                <CommandEmpty>
                                                    {isSearching ? 'Zoeken...' : 'Geen adressen gevonden.'}
                                                </CommandEmpty>
                                                {addressResults.length > 0 && (
                                                    <CommandGroup>
                                                        {addressResults.map((address) => {
                                                            const displayAddress = `${address.Street ?? ''} ${address.Number ?? ''}${address.HouseNumberSuffix ?? ''}, ${address.ZipCode ?? ''} ${address.Locality ?? ''}`;
                                                            return (
                                                                <CommandItem
                                                                    key={address.BAGAddressIdentifier}
                                                                    value={displayAddress}
                                                                    onSelect={() => {
                                                                        const buildingNumber = address.BAGBuildingIdentifiers
                                                                            ?.filter(id => id != null)
                                                                            .sort()[0] ?? '';

                                                                        bagObjectForm.setFieldValue('BAGBuildingIdentifier', buildingNumber);
                                                                        bagObjectForm.setFieldValue('BAGAddressIdentifier', address.BAGAddressIdentifier ?? null);

                                                                        setAddressSearch(displayAddress);
                                                                        setIsPopoverOpen(false);
                                                                    }}
                                                                >
                                                                    <div className="flex flex-col">
                                                                        <span className="text-sm font-medium">{displayAddress}</span>
                                                                        {address.BAGAddressIdentifier && (
                                                                            <span className="text-xs text-muted-foreground">
                                                                                bag adress id:
                                                                                {' '}
                                                                                {address.BAGAddressIdentifier}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </CommandItem>
                                                            );
                                                        })}
                                                    </CommandGroup>
                                                )}
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                            </Label>

                            <Label label="Type object">
                                <bagObjectForm.Field name="ObjectKind">
                                    {({ handleChange, state, name }) => (
                                        <SingleOptionField
                                            name={name}
                                            handleChange={handleChange}
                                            state={state}
                                            options={[
                                                { label: 'Één Adres', value: 'Address' },
                                                { label: 'Meerdere adressen', value: 'Building' },
                                            ]}
                                            className="flex flex-row w-full"
                                            render={SingleHorizontalOptionItem}
                                            disabled={isUserOffline}
                                        />
                                    )}
                                </bagObjectForm.Field>
                            </Label>
                            <span className="text-sm text-muted-foreground">
                                Selecteer 'Één Adres' wanneer u een specifiek adres inspecteert. Selecteer 'Meerdere adressen' wanneer de inspectie meerdere adressen betreft. Bij een gebouw met één adres maakt de keuze geen verschil.
                            </span>

                            <span className="text-xs">
                                Niet gevonden wat u zocht?
                                {' '}
                                <Button variant="link" className="text-text-primary text-xs self-start p-0 underline" onClick={() => { setIsCustom(true); }}>
                                    Voeg een gebouw handmatig toe.
                                </Button>
                            </span>
                        </div>
                    )}

                    {isCustom && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <Field
                                name="street"
                                validators={{
                                    onChange:
                                        validators.editElectricalProjectObjectAddressCommand.street,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label
                                        label={t('objects.location.form.streetname.label')}
                                        className="sm:col-span-2"
                                    >
                                        <Input
                                            name={name}
                                            value={state.value}
                                            onChange={(values) => {
                                                handleChange(values.target.value);
                                            }}
                                            placeholder={t(
                                                'objects.location.form.streetname.placeholder',
                                            )}
                                            disabled={isUserOffline}

                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </Field>
                            <Field
                                name="streetNumber"
                                validators={{
                                    onChange:
                                        validators.editElectricalProjectObjectAddressCommand.streetNumber,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label
                                        label={t(
                                            'objects.location.form.streetnumber.label',
                                        )}
                                    >
                                        <Input
                                            name={name}
                                            value={state.value}
                                            onChange={(values) => {
                                                handleChange(values.target.value);
                                            }}
                                            placeholder={t(
                                                'objects.location.form.streetnumber.placeholder',
                                            )}
                                            disabled={isUserOffline}

                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </Field>
                            <Field
                                name="postalCode"
                                validators={{
                                    onChange:
                                        validators.editElectricalProjectObjectAddressCommand.postalCode,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label
                                        label={t(
                                            'objects.location.form.postalcode.label',
                                        )}
                                    >
                                        <Input
                                            name={name}
                                            value={state.value}
                                            onChange={(values) => {
                                                handleChange(values.target.value);
                                            }}
                                            placeholder={t(
                                                'objects.location.form.postalCode.placeholder',
                                            )}
                                            disabled={isUserOffline}

                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </Field>
                            <Field
                                name="locality"
                                validators={{
                                    onChange:
                                        validators.editElectricalProjectObjectAddressCommand.locality,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label
                                        label={t(
                                            'objects.location.form.locality.label',
                                        )}
                                        className="sm:col-span-2"
                                    >
                                        <Input
                                            name={name}
                                            value={state.value}
                                            onChange={(values) => {
                                                handleChange(values.target.value);
                                            }}
                                            placeholder={t(
                                                'objects.location.form.locality.placeholder',
                                            )}
                                            disabled={isUserOffline}

                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </Field>

                            <span className="col-span-3 text-sm">
                                Wilt u zoeken via Kadaster?
                                {' '}
                                <Button variant="link" className="text-text-primary text-sm self-start p-0 underline" onClick={() => { setIsCustom(false); }}>
                                    Gebruik dan de zoekfunctie.
                                </Button>
                            </span>
                        </div>
                    )}
                </PopoutBody>
                <PopoutFooter>
                    <Button
                        className="w-full"
                        onClick={handleClose}
                        disabled={editAddressMutation.isPending || objectBagMutation.isPending}
                        variant="outline"
                    >
                        Annuleren
                    </Button>
                    <Button
                        className="w-full"
                        onClick={
                            () =>
                                isCustom ? handleSubmit() : bagObjectForm.handleSubmit()
                        }
                        disabled={editAddressMutation.isPending || objectBagMutation.isPending}
                    >
                        {(editAddressMutation.isPending || objectBagMutation.isPending) && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        Opslaan
                    </Button>
                </PopoutFooter>
            </PopoutContent>
        </Popout>

    );
}

function OtherField({ label, item }: { label: string; item: components['schemas']['ListElectricalProjectSelectListItemQueryResultItem'] }) {
    const [value, setValue] = useState<string>(item?.CustomValue || '');
    const params = Route.useParams();

    const patchItemMutation = api.useMutation('patch', '/api/v2/electrical/projects/selectlists/items/patch', {
        onSuccess: () => {
            queryClient.invalidateQueries(elektraAppQueryOptions.project.selectListsItems.selectListsItemsGetOptions({ electricalProjectVersionId: params.projectVersionId, listKind: item.ListKind! }));
        },
    });

    return (
        <ElseField>
            <Label label={label} key={item.ElectricalProjectSelectListItemId} className="w-full">
                <Input
                    value={value}
                    onChange={async (e) => {
                        patchItemMutation.mutateAsync({
                            body: {
                                ElectricalProjectVersionId: params.projectVersionId,
                                ListKind: item.ListKind!,
                                Items: [{
                                    ElectricalProjectSelectListItemId: item.ElectricalProjectSelectListItemId!,
                                    CustomValue: e.target.value,
                                    Selected: item.Selected,
                                }],
                            },
                        });

                        setValue(e.target.value);
                    }}
                    placeholder={label}
                />
            </Label>
        </ElseField>
    );
}

type Scope12ObjectFormValues = {
    pvPanelCount: number | null;
    pvInverterCount: number | null;
    pvPanelsTotalPower: number | null;
    pvInvertersTotalPower: number | null;
    pvBuildKind: components['schemas']['ElectricalPVInstallationBuildKind'] | null;
    pvInstallationConstructionYear: number | null;
    pvSpecialSpaceKind: components['schemas']['ElectricalPVInstallationSpecialSpacesKind'] | null;
    pvEarthingSystemKind: components['schemas']['ElectricalEarthingSystemKind'] | null;
    pvMainsVoltage: number | null;
    pvInstallationHasLightningProtection: boolean | null;
    pvInspectionCoverEntireInstallation: boolean | null;
    pVAreSpecialSpacesPresent: boolean | null;
    pVInstallationExludedParts: string;
};

function Scope12ObjectForm() {
    const { t } = useTranslation('applications');

    const isUserOffline = useOffline();

    const params = Route.useParams();

    const objectInformation = useQuery(elektraAppQueryOptions.project.request.objectOptions({
        electricalProjectVersionId: params.projectVersionId,
    }));
    const objectInformationData = objectInformation?.data?.ProjectObject;

    const defaultValues: Scope12ObjectFormValues = {
        pvPanelCount: objectInformationData?.PVPanelCount ?? null,
        pvInverterCount: objectInformationData?.PVInverterCount ?? null,
        pvPanelsTotalPower: objectInformationData?.PVPanelsTotalPower ?? null,
        pvInvertersTotalPower: objectInformationData?.PVInvertersTotalPower ?? null,
        pvBuildKind: objectInformationData?.PVBuildKind ?? null,
        pvInstallationConstructionYear: objectInformationData?.PVInstallationConstructionYear ?? null,
        pvSpecialSpaceKind: objectInformationData?.PVSpecialSpaceKind ?? null,
        pvEarthingSystemKind: objectInformationData?.PVEarthingSystemKind ?? null,
        pvMainsVoltage: objectInformationData?.PVMainsVoltage ?? null,
        pvInstallationHasLightningProtection: booleanDefaultValue(objectInformationData?.PVInstallationHasLightningProtection),
        pvInspectionCoverEntireInstallation: booleanDefaultValue(objectInformationData?.PVInspectionCoverEntireInstallation),
        pVAreSpecialSpacesPresent: booleanDefaultValue(objectInformationData?.PVAreSpecialSpacesPresent),
        pVInstallationExludedParts: objectInformationData?.PVInstallationExludedParts || '',
    };

    const { patchField } = usePatcher('/api/v2/electrical/projects/objects/patch', {
        onMutate: async (variable) => {
            let previousData: components['schemas']['EditElectricalProjectObjectQueryResult'];
            await queryClient.cancelQueries({
                queryKey: elektraAppQueryOptions.project.request.objectOptions({
                    electricalProjectVersionId: params.projectVersionId,
                }).queryKey,
            });

            queryClient.setQueryData<components['schemas']['EditElectricalProjectObjectQueryResult']>(
                elektraAppQueryOptions.project.request.objectOptions({
                    electricalProjectVersionId: params.projectVersionId,
                }).queryKey,
                (old) => {
                    previousData = old as components['schemas']['EditElectricalProjectObjectQueryResult'];
                    if (old && variable) {
                        const updatedData: components['schemas']['EditElectricalProjectObjectQueryResult'] = {
                            ProjectObject: {
                                ...previousData?.ProjectObject,
                                ...variable.body,
                                ElectricalProjectVersionId: params.projectVersionId,
                            },
                        };
                        return updatedData;
                    }
                    return old;
                },
            );

            // @ts-expect-error TODO: fix this
            return { previousData };
        },
        onSettled: () => {
            queryClient.invalidateQueries(elektraAppQueryOptions.project.request.objectOptions({
                electricalProjectVersionId: params.projectVersionId,
            }));
            queryClient.invalidateQueries(elektraAppQueryOptions.project.recapOptions({
                electricalProjectVersionId: params.projectVersionId,
            }));
        },
    }, { body: { ElectricalProjectVersionId: params.projectVersionId } });

    const objectForm = useForm<Scope12ObjectFormValues, ZodValidator>({
        defaultValues,
        validatorAdapter: zodValidator(),
    });

    return (
        <Section>
            <Section.Header>
                <Section.Title>
                    {t('objects.layout.navigation.scope.label')}
                    {' '}
                    Scope 12
                </Section.Title>
            </Section.Header>
            <Section.Content>
                <objectForm.Field
                    name="pvPanelCount"
                    validators={{
                        onChange:
                        validators.patchElectricalProjectObjectCommand.pVPanelCount,
                        onChangeAsync: patchField('pvPanelCount'),
                        onChangeAsyncDebounceMs: debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label
                            label={t(
                                'objects.scope.form.pVPanelCount.label',
                            )}
                        >
                            <NumberField
                                name={name}
                                value={state.value}
                                onChange={handleChange}
                                placeholder={t('objects.scope.form.pVPanelCount.placeholder')}
                                disabled={isUserOffline}

                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </objectForm.Field>
                <objectForm.Field
                    name="pvInverterCount"
                    validators={{
                        onChange:
                        validators.patchElectricalProjectObjectCommand.pVInverterCount,
                        onChangeAsync: patchField('pvInverterCount'),
                        onChangeAsyncDebounceMs: debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label
                            label={t(
                                'objects.scope.form.pVInverterCount.label',
                            )}
                        >
                            <NumberField
                                name={name}
                                value={state.value}
                                onChange={handleChange}
                                placeholder={t('objects.scope.form.pVInverterCount.placeholder')}
                                disabled={isUserOffline}

                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </objectForm.Field>
                <objectForm.Field
                    name="pvPanelsTotalPower"
                    validators={{
                        onChange:
                        validators.patchElectricalProjectObjectCommand.pVPanelsTotalPower,
                        onChangeAsync: patchField('pvPanelsTotalPower'),
                        onChangeAsyncDebounceMs: debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label
                            label={t(
                                'objects.scope.form.pVPanelsTotalPower.label',
                            )}
                        >
                            <NumberField
                                name={name}
                                value={state.value}
                                onChange={handleChange}
                                placeholder={t('objects.scope.form.pVPanelsTotalPower.placeholder')}
                                disabled={isUserOffline}

                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </objectForm.Field>
                <objectForm.Field

                    name="pvInvertersTotalPower"
                    validators={{
                        onChange:
                        validators.patchElectricalProjectObjectCommand.pVInvertersTotalPower,
                        onChangeAsync: patchField('pvInvertersTotalPower'),
                        onChangeAsyncDebounceMs: debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label
                            label={t(
                                'objects.scope.form.pVInvertersTotalPower.label',
                            )}
                        >
                            <NumberField
                                name={name}
                                value={state.value}
                                onChange={handleChange}
                                placeholder={t('objects.scope.form.pVInvertersTotalPower.placeholder')}
                                disabled={isUserOffline}

                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </objectForm.Field>
                <objectForm.Field
                    name="pvBuildKind"
                    validators={{
                        onChange:
                        validators.patchElectricalProjectObjectCommand.pVBuildKind,
                        onChangeAsync: patchField('pvBuildKind'),
                        onChangeAsyncDebounceMs: debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label
                            label={t(
                                'objects.scope.form.pVBuildKind.label',
                            )}
                        >
                            <SingleOptionField
                                name={name}
                                handleChange={handleChange}
                                state={state}
                                options={[
                                    {
                                        label: t('objects.scope.form.pVBuildKind.options.InRoof'),
                                        value: 'InRoof',
                                    },
                                    {
                                        label: t('objects.scope.form.pVBuildKind.options.OnField'),
                                        value: 'OnField',
                                    },
                                    {
                                        label: t('objects.scope.form.pVBuildKind.options.OnRoof'),
                                        value: 'OnRoof',
                                    },
                                    {
                                        label: t('objects.scope.form.pVBuildKind.options.OnWater'),
                                        value: 'OnWater',
                                    },
                                ]}
                                render={SingleCheckOptionItem}
                                disabled={isUserOffline}
                                dropdown
                                closeOnClick
                                placeholder={t('objects.scope.form.pVBuildKind.placeholder')}

                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </objectForm.Field>
                <objectForm.Field
                    name="pvInstallationHasLightningProtection"
                    validators={{
                        onChange:
                        validators.patchElectricalProjectObjectCommand.pVInstallationHasLightningProtection,
                        onChangeAsync: patchField('pvInstallationHasLightningProtection'),
                        onChangeAsyncDebounceMs: debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label
                            label={t(
                                'objects.scope.form.pVInstallationHasLightningProtection.label',
                            )}
                        >
                            <SingleOptionField
                                name={name}
                                handleChange={handleChange}
                                className="flex flex-row w-full justify-between"
                                state={state}
                                options={[
                                    {
                                        label: 'Ja',
                                        value: true,
                                    },
                                    {
                                        label: 'Nee',
                                        value: false,
                                    },
                                ]}
                                render={SingleHorizontalOptionItem}
                                disabled={isUserOffline}

                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </objectForm.Field>
                <objectForm.Field
                    name="pvInspectionCoverEntireInstallation"
                    validators={{
                        onChange:
                        validators.patchElectricalProjectObjectCommand.pVInspectionCoverEntireInstallation,
                        onChangeAsync: patchField('pvInspectionCoverEntireInstallation'),
                        onChangeAsyncDebounceMs: debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label
                            label={t(
                                'objects.scope.form.pVInspectionCoverEntireInstallation.label',
                            )}
                        >
                            <SingleOptionField
                                name={name}
                                handleChange={handleChange}
                                className="flex flex-row w-full justify-between"
                                state={state}
                                options={[
                                    {
                                        label: 'Ja',
                                        value: true,
                                    },
                                    {
                                        label: 'Nee',
                                        value: false,
                                    },
                                ]}
                                render={SingleHorizontalOptionItem}
                                disabled={isUserOffline}

                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </objectForm.Field>
                <objectForm.Subscribe selector={state => state.values.pvInspectionCoverEntireInstallation}>
                    {pvInspectionCoverEntireInstallation => (
                        pvInspectionCoverEntireInstallation && (
                            <ElseField>
                                <objectForm.Field
                                    name="pVInstallationExludedParts"
                                    validators={{
                                        onChange:
                                        validators.patchElectricalProjectObjectCommand.pVInstallationExludedParts,
                                        onChangeAsync: patchField('pVInstallationExludedParts'),
                                        onChangeAsyncDebounceMs: debounceTime,
                                    }}
                                >
                                    {({ handleChange, state, name }) => (
                                        <Label
                                            label={t(
                                                'objects.scope.form.pVInstallationExludedParts.label',
                                            )}
                                            className="w-full"
                                        >
                                            <Textarea
                                                name={name}
                                                value={state.value}
                                                onChange={e => handleChange(e.target.value)}
                                                placeholder={t('objects.scope.form.pVInstallationExludedParts.placeholder')}
                                                disabled={isUserOffline}

                                            />
                                            <FieldInfo state={state} />
                                        </Label>
                                    )}
                                </objectForm.Field>
                            </ElseField>
                        )
                    )}
                </objectForm.Subscribe>

                <objectForm.Field
                    name="pvInstallationConstructionYear"
                    validators={{
                        onChange:
                        validators.patchElectricalProjectObjectCommand.pVInstallationConstructionYear,
                        onChangeAsync: patchField('pvInstallationConstructionYear'),
                        onChangeAsyncDebounceMs: debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label
                            label={t(
                                'objects.scope.form.pVInstallationConstructionYear.label',
                            )}
                        >
                            <NumberField
                                name={name}
                                value={state.value}
                                onChange={handleChange}
                                placeholder={t('objects.scope.form.pVInstallationConstructionYear.placeholder')}
                                disabled={isUserOffline}

                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </objectForm.Field>
                <objectForm.Field
                    name="pVAreSpecialSpacesPresent"
                    validators={{
                        onChange:
                        validators.patchElectricalProjectObjectCommand.pVAreSpecialSpacesPresent,
                        onChangeAsync: patchField('PVAreSpecialSpacesPresent'),
                        onChangeAsyncDebounceMs: debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label
                            label={t(
                                'objects.scope.form.pVAreSpecialSpacesPresent.label',
                            )}
                        >
                            <SingleOptionField
                                name={name}
                                handleChange={handleChange}
                                className="flex flex-row w-full justify-between"
                                state={state}
                                options={[
                                    {
                                        label: 'Ja',
                                        value: true,
                                    },
                                    {
                                        label: 'Nee',
                                        value: false,
                                    },
                                ]}
                                render={SingleHorizontalOptionItem}
                                disabled={isUserOffline}

                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </objectForm.Field>

                <objectForm.Field
                    name="pvEarthingSystemKind"
                    validators={{
                        onChange:
                        validators.patchElectricalProjectObjectCommand.pVEarthingSystemKind,
                        onChangeAsync: patchField('pvEarthingSystemKind'),
                        onChangeAsyncDebounceMs: debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label
                            label={t(
                                'objects.scope.form.pVEarthingSystemKind.label',
                            )}
                            htmlFor="pvEarthingSystemKind"
                        >
                            <SingleOptionField
                                name={name}
                                handleChange={handleChange}
                                className="flex flex-row w-full justify-between"
                                state={state}
                                options={[
                                    {
                                        label: 'TT',
                                        value: 'TT',
                                    },
                                    {
                                        label: 'TN-S',
                                        value: 'TN-S',
                                    },
                                    {
                                        label: 'TN-CS',
                                        value: 'TN-CS',
                                    },
                                    {
                                        label: 'IT',
                                        value: 'IT',
                                    },
                                    {
                                        label: 'TN-C',
                                        value: 'TN-C',
                                    },

                                ]}
                                render={SingleHorizontalOptionItem}
                                disabled={isUserOffline}

                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </objectForm.Field>

                <objectForm.Field
                    name="pvMainsVoltage"
                    validators={{
                        onChange:
                        validators.patchElectricalProjectObjectCommand.pVMainsVoltage,
                        onChangeAsync: patchField('pvMainsVoltage'),
                        onChangeAsyncDebounceMs: debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label
                            label={t(
                                'objects.scope.form.pVMainsVoltage.label',
                            )}
                        >
                            <NumberField
                                name={name}
                                value={state.value}
                                onChange={handleChange}
                                placeholder={t('objects.scope.form.pVMainsVoltage.placeholder')}
                                disabled={isUserOffline}

                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </objectForm.Field>

            </Section.Content>
            <Footer />
        </Section>
    );
}
